import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthResponse, UserInfo } from "@/lib/types";

/**
 * Referencia estable usada por selectores que devuelven la lista de roles
 * cuando el usuario no está autenticado · evita crear `[]` nuevo en cada
 * render, lo cual rompía `useSyncExternalStore` de Zustand+React con un loop
 * infinito ("getServerSnapshot should be cached").
 */
const EMPTY_ROLES: ReadonlyArray<string> = Object.freeze([]);

/** Igual que EMPTY_ROLES · referencia estable para los permisos vacíos. */
const EMPTY_PERMISOS: ReadonlyArray<string> = Object.freeze([]);

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: UserInfo | null;
  isHydrated: boolean;
  setSession: (auth: AuthResponse) => void;
  setAccessToken: (token: string) => void;
  /** Actualiza datos del usuario en sesión (ej. tras editar Mi perfil). */
  updateUser: (partial: Partial<UserInfo>) => void;
  clear: () => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isHydrated: false,
      setSession: (auth) =>
        set({
          accessToken: auth.accessToken,
          refreshToken: auth.refreshToken,
          user: auth.user,
        }),
      setAccessToken: (token) => set({ accessToken: token }),
      updateUser: (partial) =>
        set((s) => (s.user ? { user: { ...s.user, ...partial } } : {})),
      clear: () => set({ accessToken: null, refreshToken: null, user: null }),
      setHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: "multiva-auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    }
  )
);

/**
 * Hook con referencia estable para acceder a los roles del usuario.
 * Reemplaza el patrón `useAuthStore((s) => s.user?.roles ?? [])` que
 * crea un array nuevo en cada render y dispara loops infinitos.
 */
export function useUserRoles(): ReadonlyArray<string> {
  return useAuthStore((s) => s.user?.roles ?? EMPTY_ROLES);
}

/**
 * Permisos efectivos del usuario (RBAC · Ruta B). Referencia estable para
 * evitar loops de `useSyncExternalStore`. Sesiones persistidas ANTES de la
 * feature no traen `permisos` → cae a un arreglo vacío estable hasta el
 * siguiente login/refresh (el gateo de UI es cosmético; el backend manda).
 */
export function useUserPermisos(): ReadonlyArray<string> {
  return useAuthStore((s) => s.user?.permisos ?? EMPTY_PERMISOS);
}
